import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaFileDownload, FaCheckCircle, FaFileInvoice } from 'react-icons/fa';
import Swal from 'sweetalert2';
import declaracionRentaService from '../../services/declaracionRentaService';
import '../../styles/components/DJ1879HonorariosTab.css';

/**
 * Componente para gestionar DJ1879 - Honorarios y Retenciones
 *
 * Permite crear, editar, eliminar registros y generar CSV con formato SII
 * Diseñado para MCP (Model Context Protocol)
 *
 * @param {Object} props
 * @param {number} props.declaracionId - ID de la declaración jurada
 * @param {number} props.year - Año de la declaración
 * @param {number} props.idCompany - ID de la empresa
 * @param {string} props.rutEmpresa - RUT de la empresa
 * @param {string} props.token - Token de autenticación
 */
const DJ1879HonorariosTab = ({
    declaracionId,
    year,
    idCompany,
    rutEmpresa,
    token
}) => {
    const [registros, setRegistros] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingRegistro, setEditingRegistro] = useState(null);
    const [validationResult, setValidationResult] = useState(null);

    const [formData, setFormData] = useState({
        rut_receptor: '',
        monto_honorarios_tasa_1375: '',
        monto_directores_tasa_10: '',
        monto_directores_tasa_35: '',
        monto_isla: '',
        '3%_prestamo': '',
        numero_certificado: '',
        enero: '',
        febrero: '',
        marzo: '',
        abril: '',
        mayo: '',
        junio: '',
        julio: '',
        agosto: '',
        septiembre: '',
        octubre: '',
        noviembre: '',
        diciembre: ''
    });

    // Cargar registros al montar el componente
    useEffect(() => {
        loadRegistros();
    }, [declaracionId]);

    const loadRegistros = async () => {
        try {
            setLoading(true);
            const data = await declaracionRentaService.getHonorarios(
                declaracionId,
                idCompany,
                token
            );
            setRegistros(data);
        } catch (error) {
            console.error('Error al cargar registros:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error al cargar los registros'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (registro = null) => {
        if (registro) {
            setEditingRegistro(registro);
            setFormData({

                rut_receptor: registro.rut_receptor || '',
                monto_honorarios_tasa_1375: registro.monto_honorarios_tasa_1375 || '',
                monto_directores_tasa_10: registro.monto_directores_tasa_10 || '',
                monto_directores_tasa_35: registro.monto_directores_tasa_35 || '',
                monto_isla: registro.monto_isla || '',
                '3%_prestamo': registro['3%_prestamo'] || '',
                numero_certificado: registro.numero_certificado || '',
                enero: registro.enero || '',
                febrero: registro.febrero || '',
                marzo: registro.marzo || '',
                abril: registro.abril || '',
                mayo: registro.mayo || '',
                junio: registro.junio || '',
                julio: registro.julio || '',
                agosto: registro.agosto || '',
                septiembre: registro.septiembre || '',
                octubre: registro.octubre || '',
                noviembre: registro.noviembre || '',
                diciembre: registro.diciembre || ''
            });
        } else {
            setEditingRegistro(null);
            setFormData({

                rut_receptor: '',
                monto_honorarios_tasa_1375: '',
                monto_directores_tasa_10: '',
                monto_directores_tasa_35: '',
                monto_isla: '',
                '3%_prestamo': '',
                numero_certificado: '',
                enero: '',
                febrero: '',
                marzo: '',
                abril: '',
                mayo: '',
                junio: '',
                julio: '',
                agosto: '',
                septiembre: '',
                octubre: '',
                noviembre: '',
                diciembre: ''
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingRegistro(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validaciones básicas
        if (!formData.rut_receptor) {
            Swal.fire({
                icon: 'warning',
                title: 'Campo requerido',
                text: 'Debe ingresar el RUT del receptor'
            });
            return;
        }

        try {
            if (editingRegistro) {
                await declaracionRentaService.updateHonorario(
                    editingRegistro.id,
                    formData,
                    idCompany,
                    token
                );
                Swal.fire({
                    icon: 'success',
                    title: 'Actualizado',
                    text: 'Registro actualizado exitosamente'
                });
            } else {
                await declaracionRentaService.createHonorario(
                    declaracionId,
                    formData,
                    idCompany,
                    token
                );
                Swal.fire({
                    icon: 'success',
                    title: 'Creado',
                    text: 'Registro creado exitosamente'
                });
            }
            handleCloseModal();
            loadRegistros();
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'Error al guardar registro'
            });
        }
    };

    const handleDelete = async (registro) => {
        const result = await Swal.fire({
            icon: 'warning',
            title: '¿Está seguro?',
            text: 'Esta acción no se puede deshacer',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await declaracionRentaService.deleteHonorario(
                    registro.id,
                    idCompany,
                    token
                );
                Swal.fire({
                    icon: 'success',
                    title: 'Eliminado',
                    text: 'Registro eliminado exitosamente'
                });
                loadRegistros();
            } catch (error) {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: error.message || 'Error al eliminar registro'
                });
            }
        }
    };

    const handleValidate = async () => {
        try {
            const result = await declaracionRentaService.validateDJ1879Data(
                declaracionId,
                idCompany,
                token
            );
            setValidationResult(result);

            if (result.valid) {
                Swal.fire({
                    icon: 'success',
                    title: 'Validación exitosa',
                    text: `Todos los ${result.totalRegistros} registros son válidos`
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Errores de validación',
                    html: `<ul style="text-align: left;">${result.errors.map(e => `<li>${e}</li>`).join('')}</ul>`
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error al validar datos'
            });
        }
    };

    const handleDownloadCSV = async () => {
        if (registros.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Sin datos',
                text: 'No hay registros para generar el CSV'
            });
            return;
        }

        try {
            // Primero validar
            const validation = await declaracionRentaService.validateDJ1879Data(
                declaracionId,
                idCompany,
                token
            );

            if (!validation.valid) {
                const result = await Swal.fire({
                    icon: 'warning',
                    title: 'Errores de validación',
                    html: `Se encontraron errores:<br/><ul style="text-align: left;">${validation.errors.map(e => `<li>${e}</li>`).join('')}</ul><br/>¿Desea descargar el CSV de todas formas?`,
                    showCancelButton: true,
                    confirmButtonText: 'Sí, descargar',
                    cancelButtonText: 'Cancelar'
                });

                if (!result.isConfirmed) {
                    return;
                }
            }

            // Descargar CSV
            await declaracionRentaService.downloadAndSaveDJ1879CSV(
                declaracionId,
                idCompany,
                rutEmpresa,
                year,
                token
            );

            Swal.fire({
                icon: 'success',
                title: 'Descarga exitosa',
                text: 'El archivo CSV se ha descargado correctamente'
            });
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'Error al descargar CSV'
            });
        }
    };

    const getTotalRetenido = (registro) => {

        const total =
            parseFloat(registro.monto_honorarios_tasa_1375 || 0) +
            parseFloat(registro.monto_directores_tasa_10 || 0) +
            parseFloat(registro.monto_directores_tasa_35 || 0) +
            parseFloat(registro.monto_isla || 0) +
            parseFloat(registro['3%_prestamo'] || 0);
        return total;
    };

    return (
        <div className="dj1879-tab">
            {/* Header */}
            <div className="tab-header">
                <div className="tab-title">
                    <FaFileInvoice className="title-icon" />
                    <div>
                        <h2>DJ 1879 - Retenciones del Impuesto Adicional</h2>
                        <p className="subtitle">Año {year} | Total: {registros.length} registros</p>
                    </div>
                </div>
                <div className="header-actions">
                    <button
                        className="btn-validate"
                        onClick={handleValidate}
                        disabled={loading || registros.length === 0}
                    >
                        <FaCheckCircle /> Validar Datos
                    </button>
                    <button
                        className="btn-download"
                        onClick={handleDownloadCSV}
                        disabled={loading || registros.length === 0}
                    >
                        <FaFileDownload /> Descargar CSV
                    </button>
                    <button
                        className="btn-nuevo"
                        onClick={() => handleOpenModal()}
                        disabled={loading}
                    >
                        <FaPlus /> Nuevo Registro
                    </button>
                </div>
            </div>

            {/* Información del formato CSV */}
            <div className="info-box">
                <h4>Formato CSV según SII:</h4>
                <ul>
                    <li>Separador: punto y coma (;)</li>
                    <li>RUT sin guiones, K en mayúscula</li>
                    <li>Números sin separadores de miles</li>
                    <li>Encoding: UTF-8 (MS-DOS)</li>
                </ul>
            </div>

            {/* Tabla de registros */}
            <div className="table-container">
                {loading ? (
                    <div className="loading-message">Cargando...</div>
                ) : registros.length === 0 ? (
                    <div className="empty-message">
                        <FaFileInvoice className="empty-icon" />
                        <p>No hay honorarios registrados</p>
                        <button className="btn-primary" onClick={() => handleOpenModal()}>
                            <FaPlus /> Agregar Primer Registro
                        </button>
                    </div>
                ) : (
                    <table className="registros-table">
                        <thead>
                            <tr>
                                <th>N°</th>
                                <th>RUT Receptor</th>
                                <th>Honorarios Total</th>
                                <th>Directores Total</th>
                                <th>Total Retenido</th>
                                <th>N° Certificado</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {registros.map((registro) => (
                                <tr key={registro.id}>
                                    <td>{registro.numero}</td>
                                    <td>{registro.rut_receptor}</td>
                                    <td>
                                        ${(
                                            parseFloat(registro.monto_honorarios_tasa_1375 || 0) +
                                            parseFloat(registro.monto_honorarios_tasa_10 || 0) +
                                            parseFloat(registro.monto_honorarios_tasa_35 || 0)
                                        ).toLocaleString('es-CL')}
                                    </td>
                                    <td>
                                        ${(
                                            parseFloat(registro.monto_directores_tasa_1375 || 0) +
                                            parseFloat(registro.monto_directores_tasa_10 || 0) +
                                            parseFloat(registro.monto_directores_tasa_35 || 0) +
                                            parseFloat(registro.monto_directores_tasa_otra || 0)
                                        ).toLocaleString('es-CL')}
                                    </td>
                                    <td>${getTotalRetenido(registro).toLocaleString('es-CL')}</td>
                                    <td>{registro.numero_certificado || '-'}</td>
                                    <td className="actions-cell">
                                        <button
                                            className="btn-icon btn-edit"
                                            onClick={() => handleOpenModal(registro)}
                                            title="Editar"
                                        >
                                            <FaEdit />
                                        </button>
                                        <button
                                            className="btn-icon btn-delete"
                                            onClick={() => handleDelete(registro)}
                                            title="Eliminar"
                                        >
                                            <FaTrash />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modal de formulario */}
            {showModal && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{editingRegistro ? 'Editar' : 'Nuevo'} Honorario</h3>
                            <button className="btn-close" onClick={handleCloseModal}>×</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="form-sections">
                                {/* Datos del Receptor */}
                                <div className="form-section">
                                    <h4>Datos del Receptor</h4>
                                    <div className="form-grid">
                                        <div className="form-group">
                                            <label htmlFor="rut_receptor">RUT Receptor *</label>
                                            <input
                                                type="text"
                                                id="rut_receptor"
                                                name="rut_receptor"
                                                value={formData.rut_receptor}
                                                onChange={handleInputChange}
                                                required
                                                placeholder="Ej: 12345678-9"
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="numero_certificado">Número Certificado</label>
                                            <input
                                                type="number"  // ✅ CORREGIDO
                                                id="numero_certificado"
                                                name="numero_certificado"
                                                value={formData.numero_certificado}
                                                onChange={handleInputChange}
                                                min="1"
                                                step="1"
                                                placeholder="1"
                                            />
                                        </div>

                                    </div>
                                </div>

                                {/* Montos */}
                                <div className="form-section">
                                    <h4>Montos de Retención</h4>
                                    <div className="form-grid form-grid-3">
                                        <div className="form-group">
                                            <label htmlFor="monto_honorarios_tasa_1375">Honorarios Tasa 13.75%</label>
                                            <input
                                                type="number"
                                                id="monto_honorarios_tasa_1375"
                                                name="monto_honorarios_tasa_1375"
                                                value={formData.monto_honorarios_tasa_1375}
                                                onChange={handleInputChange}
                                                min="0"
                                                step="0.01"
                                                placeholder="0"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="monto_directores_tasa_10">Directores Tasa 10%</label>
                                            <input
                                                type="number"
                                                id="monto_directores_tasa_10"
                                                name="monto_directores_tasa_10"
                                                value={formData.monto_directores_tasa_10}
                                                onChange={handleInputChange}
                                                min="0"
                                                step="0.01"
                                                placeholder="0"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="monto_directores_tasa_35">Directores Tasa 35%</label>
                                            <input
                                                type="number"
                                                id="monto_directores_tasa_35"
                                                name="monto_directores_tasa_35"
                                                value={formData.monto_directores_tasa_35}
                                                onChange={handleInputChange}
                                                min="0"
                                                step="0.01"
                                                placeholder="0"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="monto_isla">Monto Isla</label>
                                            <input
                                                type="number"
                                                id="monto_isla"
                                                name="monto_isla"
                                                value={formData.monto_isla}
                                                onChange={handleInputChange}
                                                min="0"
                                                step="0.01"
                                                placeholder="0"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="3%_prestamo">3% Préstamo</label>
                                            <input
                                                type="number"
                                                id="3%_prestamo"
                                                name="3%_prestamo"
                                                value={formData['3%_prestamo']}
                                                onChange={handleInputChange}
                                                min="0"
                                                step="0.01"
                                                placeholder="0"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Meses del año */}
                                <div className="form-section">
                                    <h4>Distribución Mensual</h4>
                                    <div className="form-grid form-grid-4">
                                        <div className="form-group">
                                            <label htmlFor="enero">Enero</label>
                                            <input
                                                type="text"  
                                                id="enero"
                                                name="enero"
                                                value={formData.enero}
                                                onChange={handleInputChange}
                                                placeholder=""
                                            />
                                            
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="febrero">Febrero</label>
                                            <input
                                                type="text"
                                                id="febrero"
                                                name="febrero"
                                                value={formData.febrero}
                                                onChange={handleInputChange}
                                        
                                                placeholder=""
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="marzo">Marzo</label>
                                            <input
                                                type="text"
                                                id="marzo"
                                                name="marzo"
                                                value={formData.marzo}
                                                onChange={handleInputChange}
                                                
                                                placeholder=""
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="abril">Abril</label>
                                            <input
                                                type="text"
                                                id="abril"
                                                name="abril"
                                                value={formData.abril}
                                                onChange={handleInputChange}
                                                
                                                placeholder=""
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="mayo">Mayo</label>
                                            <input
                                                type="text"
                                                id="mayo"
                                                name="mayo"
                                                value={formData.mayo}
                                                onChange={handleInputChange}
                                                
                                                placeholder=""
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="junio">Junio</label>
                                            <input
                                                type="text"
                                                id="junio"
                                                name="junio"
                                                value={formData.junio}
                                                onChange={handleInputChange}
                                                
                                                placeholder=""
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="julio">Julio</label>
                                            <input
                                                type="text"
                                                id="julio"
                                                name="julio"
                                                value={formData.julio}
                                                onChange={handleInputChange}
                                                
                                                placeholder=""
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="agosto">Agosto</label>
                                            <input
                                                type="text"
                                                id="agosto"
                                                name="agosto"
                                                value={formData.agosto}
                                                onChange={handleInputChange}
                                                
                                                placeholder=""
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="septiembre">Septiembre</label>
                                            <input
                                                type="text"
                                                id="septiembre"
                                                name="septiembre"
                                                value={formData.septiembre}
                                                onChange={handleInputChange}
                                               
                                                placeholder=""
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="octubre">Octubre</label>
                                            <input
                                                type="text"
                                                id="octubre"
                                                name="octubre"
                                                value={formData.octubre}
                                                onChange={handleInputChange}
                                               
                                                placeholder=""
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="noviembre">Noviembre</label>
                                            <input
                                                type="text"
                                                id="noviembre"
                                                name="noviembre"
                                                value={formData.noviembre}
                                                onChange={handleInputChange}
                                                
                                                placeholder=""
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="diciembre">Diciembre</label>
                                            <input
                                                type="text"
                                                id="diciembre"
                                                name="diciembre"
                                                value={formData.diciembre}
                                                onChange={handleInputChange}
                                                
                                                placeholder=""
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-primary">
                                    {editingRegistro ? 'Actualizar' : 'Crear'} Registro
                                </button>
                            </div>
                            
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DJ1879HonorariosTab;
